import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { SERVICES } from "@/data/services";
import { ArrowRight, CloudArrowUp, CheckCircle, FileText, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 25;

export default function FreeTrial() {
  const [params] = useSearchParams();
  const pre = params.get("service") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    service_type: pre,
    project_description: "",
  });
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (pre) setForm((f) => ({ ...f, service_type: pre }));
  }, [pre]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    handleFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v || ""));
      if (file) data.append("file", file);
      await axios.post(`${API}/free-trial`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Trial request received. We'll be in touch!");
      setSuccess(true);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="free-trial-page">
      <section className="relative bg-zinc-50 border-b border-zinc-200 overflow-hidden ruler-top">
        <div className="absolute inset-0 bg-blueprint-grid pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
            Free Trial · No card required
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium text-zinc-950 max-w-4xl leading-[0.95]">
            Upload a sample. Get a drawing back — free.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl leading-relaxed">
            Send us a point file, field notes, or a design .dwg. We'll draft a sample
            deliverable at no cost so you can judge our quality against your standard.
          </p>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          {success ? (
            <div
              className="border border-zinc-900 p-12 bg-zinc-50 text-center"
              data-testid="freetrial-success"
            >
              <CheckCircle size={44} weight="duotone" className="text-[#0044FF] mx-auto" />
              <h2 className="mt-6 font-display text-4xl tracking-tight text-zinc-950">
                Got it — your trial is underway.
              </h2>
              <p className="mt-4 text-zinc-600 max-w-xl mx-auto">
                We'll review your sample and reach out within 1 business day with a
                drafted deliverable or clarifying questions.
              </p>
              <div className="mt-8 flex gap-3 justify-center">
                <Link to="/services" className="px-5 py-3 border border-zinc-900 text-zinc-950 font-medium btn-lift hover:bg-zinc-900 hover:text-white">
                  Explore services
                </Link>
                <Link to="/" className="px-5 py-3 bg-[#0044FF] text-white font-medium btn-lift">
                  Back to home
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="border border-zinc-300 bg-white p-6 md:p-10"
              data-testid="freetrial-form"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Name" name="name" value={form.name} onChange={onChange} required testId="ft-name" />
                <Field label="Work email" name="email" type="email" value={form.email} onChange={onChange} required testId="ft-email" />
                <div className="md:col-span-2">
                  <Field label="Company" name="company" value={form.company} onChange={onChange} testId="ft-company" />
                </div>
              </div>

              <div className="mt-8">
                <label
                  htmlFor="service_type"
                  className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2"
                >
                  Survey type
                </label>
                <select
                  id="service_type"
                  name="service_type"
                  value={form.service_type}
                  onChange={onChange}
                  className="w-full border border-zinc-300 bg-white p-3 text-sm focus:outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/20"
                  data-testid="ft-service-type"
                >
                  <option value="">— Choose a service —</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      {s.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-8">
                <label
                  htmlFor="project_description"
                  className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2"
                >
                  Project description
                </label>
                <textarea
                  id="project_description"
                  name="project_description"
                  rows={4}
                  value={form.project_description}
                  onChange={onChange}
                  placeholder="Brief scope, jurisdiction, deliverable expected…"
                  className="w-full border border-zinc-300 bg-white p-4 text-sm focus:outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/20"
                  data-testid="ft-project-description"
                />
              </div>

              <div className="mt-8">
                <label className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2">
                  Upload sample data <span className="normal-case tracking-normal text-zinc-400 font-sans">(max {MAX_MB} MB)</span>
                </label>

                {file ? (
                  <div
                    className="border border-zinc-900 bg-white p-5 flex items-center justify-between gap-4"
                    data-testid="ft-file-chip"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <FileText size={28} weight="duotone" className="text-[#0044FF] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-zinc-950 truncate">{file.name}</div>
                        <div className="text-xs text-zinc-500 font-mono">
                          {(file.size / 1024).toFixed(1)} KB · {file.type || "application/octet-stream"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="p-2 text-zinc-500 hover:text-zinc-900 border border-zinc-300 hover:border-zinc-900"
                      aria-label="Remove file"
                      data-testid="ft-file-remove"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="ft-file-input"
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`block border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
                      dragOver
                        ? "border-[#0044FF] bg-[#0044FF]/5"
                        : "border-zinc-300 bg-zinc-50 hover:border-[#0044FF]"
                    }`}
                    data-testid="ft-file-dropzone"
                  >
                    <CloudArrowUp size={36} weight="duotone" className="text-[#0044FF] mx-auto" />
                    <div className="mt-3 font-medium text-zinc-950">
                      Drop your file here, or click to browse
                    </div>
                    <div className="mt-1 text-xs text-zinc-500 font-mono uppercase tracking-[0.2em]">
                      .csv · .txt · .dwg · .rw5 · .las · .pdf · .zip
                    </div>
                    <input
                      id="ft-file-input"
                      type="file"
                      className="hidden"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      data-testid="ft-file-input"
                    />
                  </label>
                )}
              </div>

              <div className="mt-10 flex flex-wrap gap-3 items-center justify-between">
                <p className="text-xs text-zinc-500 font-mono uppercase tracking-[0.2em]">
                  No card. No commitment.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-4 bg-[#0044FF] hover:bg-[#0033CC] text-white font-medium btn-lift inline-flex items-center gap-2 disabled:opacity-60"
                  data-testid="ft-submit"
                >
                  {submitting ? "Uploading…" : "Request free trial"}
                  <ArrowRight size={14} weight="bold" />
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label
        htmlFor={name}
        className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 block mb-2"
      >
        {label} {required && <span className="text-[#FF5500]">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full border border-zinc-300 bg-white p-3 text-sm focus:outline-none focus:border-[#0044FF] focus:ring-2 focus:ring-[#0044FF]/20"
        data-testid={testId}
      />
    </div>
  );
}
